import { useEffect, useState, memo, useMemo, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  Snackbar, Alert, CircularProgress, Paper, ClickAwayListener,
  ToggleButton, ToggleButtonGroup, RadioGroup, FormControlLabel, Radio, FormControl,
} from '@mui/material';
import { Close, Savings, Print, ArrowDownward, ArrowUpward } from '@mui/icons-material';

const PRIMARY = '#5c3d1e';
const PRIMARY_DARK = '#3b2410';

const fs = {
  '& .MuiOutlinedInput-root': { borderRadius: '4px', height: '32px' },
  '& .MuiInputLabel-root': { fontSize: '13px' },
  '& .MuiInputBase-input': { padding: '6px 10px', fontSize: '13px' },
};

const EMPTY = {
  Id: 0,
  FinYear: '',
  BillNo: '',
  BillDate: '',
  IsTypeReceipt: false, // false = Payment, true = Receipt
  Istype: true,         // true = Account Head, false = Master
  AccountHeadId: null,
  MasterId: null,
  Amount: '',
  Remarks: '',
};

const sxDialogTitle = {
  py: 1, px: 1.5,
  background: `linear-gradient(135deg, ${PRIMARY_DARK}, ${PRIMARY})`,
  color: 'white',
};
const sxDialogContent = { p: 1.5, background: '#fdf6ec' };
const sxInnerBox = { background: '#fff', borderRadius: '8px', p: 1.5, border: '1px solid #f0e8d8' };
const sxActions = {
  px: 1.5, py: 1,
  background: '#f8f9fa',
  borderTop: '1px solid #e9ecef',
  justifyContent: 'flex-end',
};
const sxRemarks = {
  '& .MuiInputLabel-root': { fontSize: '13px' },
  '& .MuiInputBase-input': { fontSize: '13px', padding: '6px 10px' },
  '& .MuiOutlinedInput-root': { borderRadius: '4px' },
};

// ── Searchable dropdown ────────────────────────────────────────────────────────
const SearchableSelect = memo(({ label, value, options, onChange, onBlur, error, helperText, disabled }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedLabel = options.find(o => o.Id === value)?.Name || '';

  const filtered = useMemo(() => {
    if (!search.trim()) return options.slice(0, 50);
    const q = search.toLowerCase();
    return options.filter(o => o.Name?.toLowerCase().includes(q)).slice(0, 50);
  }, [options, search]);

  const handleSelect = (id) => {
    onChange(id);
    setSearch('');
    setOpen(false);
  };

  const handleOpen = () => {
    if (!disabled) { setSearch(''); setOpen(true); }
  };

  return (
    <ClickAwayListener onClickAway={() => { setOpen(false); setSearch(''); if (onBlur) onBlur(); }}>
      <Box ref={containerRef} sx={{ position: 'relative' }}>
        <TextField
          fullWidth size="small"
          label={label}
          value={open ? search : selectedLabel}
          onChange={e => setSearch(e.target.value)}
          onClick={handleOpen}
          onFocus={handleOpen}
          disabled={disabled}
          error={error}
          helperText={helperText}
          placeholder={open ? 'Type to search...' : ''}
          autoComplete="off"
          sx={{
            ...fs,
            '& .MuiInputBase-input': { padding: '6px 10px', fontSize: '13px', cursor: disabled ? 'default' : 'pointer' },
          }}
        />
        {open && (
          <Paper elevation={4} sx={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            zIndex: 1400, maxHeight: 220, overflow: 'auto',
            border: '1px solid #e5e7eb', borderRadius: '4px', mt: '2px',
          }}>
            {filtered.length === 0 ? (
              <Box sx={{ p: 1.5, color: '#999', fontSize: '13px', textAlign: 'center' }}>No results found</Box>
            ) : (
              filtered.map(o => (
                <Box
                  key={o.Id}
                  onMouseDown={e => { e.preventDefault(); handleSelect(o.Id); }}
                  sx={{
                    px: 1.5, py: 0.75, fontSize: '13px', cursor: 'pointer',
                    backgroundColor: o.Id === value ? 'rgba(92,61,30,0.08)' : 'transparent',
                    fontWeight: o.Id === value ? 600 : 400,
                    '&:hover': { backgroundColor: 'rgba(92,61,30,0.06)' },
                  }}
                >
                  {o.Name}
                </Box>
              ))
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
});

// ── Main Modal ─────────────────────────────────────────────────────────────────
const PettyCashModal = memo(({
  open,
  onClose,
  modalAction,
  initialData,
  onSave,
  accountHeads,
  masters,
  saving,
  fetchMaxBillNo,
  onPrint,
}) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const isView = modalAction === 'view';

  // ── Init form on open ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setTouched({});

    if (modalAction === 'create') {
      const today = new Date().toISOString().split('T')[0];
      setForm({ ...EMPTY, BillDate: today });
      if (fetchMaxBillNo) {
        fetchMaxBillNo().then(d => {
          setForm(prev => ({ ...prev, BillNo: d.billNumber || '' }));
        }).catch(() => {});
      }
    } else if (initialData) {
      setForm({
        ...EMPTY,
        ...initialData,
        BillDate: initialData.BillDate ? initialData.BillDate.split('T')[0] : '',
        IsTypeReceipt: initialData.IsTypeReceipt ?? false,
        Istype: initialData.Istype ?? true,
        AccountHeadId: initialData.AccountHeadId ?? null,
        MasterId: initialData.MasterId ?? null,
      });
    }
  }, [open, modalAction]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = (field, value) => {
    if (field === 'BillNo' && (!value || !String(value).trim())) return 'Required';
    if (field === 'BillDate' && !value) return 'Required';
    if (field === 'AccountHeadId' && form.Istype === true && !value) return 'Required';
    if (field === 'MasterId' && form.Istype === false && !value) return 'Required';
    if (field === 'Amount' && (!value || parseFloat(value) <= 0)) return 'Must be > 0';
    return '';
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validate(field, form[field]) }));
  };

  const handleSave = () => {
    const errs = {};
    const idField = form.Istype === true ? 'AccountHeadId' : 'MasterId';
    ['BillNo', 'BillDate', idField, 'Amount'].forEach(f => {
      const e = validate(f, form[f]);
      if (e) errs[f] = e;
    });
    const touchedFields = { BillNo: true, BillDate: true, [idField]: true, Amount: true };
    setTouched(touchedFields);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setSnackbar({ open: true, message: Object.values(errs).join(' | ') });
      return;
    }
    onSave(form);
  };

  const selectedAccountHead = (accountHeads || []).find(a => a.Id === form.AccountHeadId)?.Name || '-';
  const selectedMaster = (masters || []).find(m => m.Id === form.MasterId)?.Name || '-';

  const title = modalAction === 'create'
    ? 'Create Petty Cash Entry'
    : modalAction === 'edit'
      ? 'Edit Petty Cash Entry'
      : 'View Petty Cash Entry';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '8px', maxWidth: '600px' } }}
      >
        {/* Header */}
        <DialogTitle sx={sxDialogTitle}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Savings sx={{ fontSize: 18 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{title}</Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: 'white', p: '2px' }}>
              <Close style={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={sxDialogContent}>
          <Box sx={sxInnerBox}>
            <Typography sx={{
              fontWeight: 600, color: PRIMARY, mb: 1.5, fontSize: '12px',
              textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'Times New Roman',
            }}>
              Petty Cash Information
            </Typography>

            <Grid container spacing={1.5}>
              {/* Row 1: Bill No + Date */}
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small"
                  label="Bill No *"
                  value={form.BillNo}
                  onChange={e => set('BillNo', e.target.value)}
                  onBlur={() => handleBlur('BillNo')}
                  disabled={isView}
                  error={touched.BillNo && !!errors.BillNo}
                  helperText={touched.BillNo && errors.BillNo}
                  sx={fs}
                />
              </Grid>

              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="date"
                  label="Date *"
                  value={form.BillDate}
                  onChange={e => set('BillDate', e.target.value)}
                  onBlur={() => handleBlur('BillDate')}
                  disabled={isView}
                  error={touched.BillDate && !!errors.BillDate}
                  helperText={touched.BillDate && errors.BillDate}
                  InputLabelProps={{ shrink: true }}
                  sx={fs}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="number"
                  label="Amount *"
                  value={form.Amount}
                  onChange={e => set('Amount', e.target.value)}
                  onBlur={() => handleBlur('Amount')}
                  disabled={isView}
                  error={touched.Amount && !!errors.Amount}
                  helperText={touched.Amount && errors.Amount}
                  inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                  sx={fs}
                />
              </Grid>

              {/* Row 2: Type toggle full width */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontSize: '12px', color: '#666', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Type *
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={form.IsTypeReceipt}
                    onChange={(_, val) => { if (val !== null) set('IsTypeReceipt', val); }}
                    disabled={isView}
                    size="small"
                    sx={{
                      height: '32px',
                      '& .MuiToggleButton-root': {
                        fontSize: '12px', fontWeight: 600, px: 2,
                        textTransform: 'none', border: '1px solid #e0e0e0',
                        color: '#666',
                      },
                      '& .MuiToggleButton-root.Mui-selected[value="true"]': {
                        background: '#e8f5e9', color: '#2e7d32', borderColor: '#2e7d32',
                      },
                    }}
                  >
                    <ToggleButton
                      value={true}
                      sx={{
                        '&.Mui-selected': { background: '#e8f5e9 !important', color: '#2e7d32 !important', borderColor: '#2e7d32 !important' },
                      }}
                    >
                      <ArrowDownward sx={{ fontSize: 14, mr: 0.5 }} /> Receipt
                    </ToggleButton>
                    <ToggleButton
                      value={false}
                      sx={{
                        '&.Mui-selected': { background: '#fce4ec !important', color: '#c62828 !important', borderColor: '#c62828 !important' },
                      }}
                    >
                      <ArrowUpward sx={{ fontSize: 14, mr: 0.5 }} /> Payment
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Grid>

              {/* Istype: Account Head or Master */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontSize: '12px', color: '#666', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Link To *
                  </Typography>
                  <FormControl disabled={isView}>
                    <RadioGroup
                      row
                      value={String(form.Istype)}
                      onChange={(e) => {
                        const val = e.target.value === 'true';
                        // clear the unused id field when switching
                        setForm(prev => ({
                          ...prev,
                          Istype: val,
                          AccountHeadId: val === true ? prev.AccountHeadId : null,
                          MasterId: val === false ? prev.MasterId : null,
                        }));
                        setErrors(prev => ({ ...prev, AccountHeadId: '', MasterId: '' }));
                      }}
                      sx={{ gap: 1 }}
                    >
                      <FormControlLabel
                        value="true"
                        control={
                          <Radio
                            size="small"
                            sx={{
                              p: '3px',
                              color: PRIMARY,
                              '&.Mui-checked': { color: PRIMARY },
                            }}
                          />
                        }
                        label={<Typography sx={{ fontSize: '12px', fontWeight: 500 }}>Account Head</Typography>}
                      />
                      <FormControlLabel
                        value="false"
                        control={
                          <Radio
                            size="small"
                            sx={{
                              p: '3px',
                              color: PRIMARY,
                              '&.Mui-checked': { color: PRIMARY },
                            }}
                          />
                        }
                        label={<Typography sx={{ fontSize: '12px', fontWeight: 500 }}>Master</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Grid>

              {/* Conditional: Account Head or Master dropdown */}
              <Grid item xs={12}>
                {form.Istype === true ? (
                  isView ? (
                    <TextField fullWidth size="small" label="Account Head" value={selectedAccountHead} disabled sx={fs} />
                  ) : (
                    <SearchableSelect
                      label="Account Head *"
                      value={form.AccountHeadId}
                      options={accountHeads || []}
                      onChange={val => set('AccountHeadId', val)}
                      onBlur={() => handleBlur('AccountHeadId')}
                      error={touched.AccountHeadId && !!errors.AccountHeadId}
                      helperText={touched.AccountHeadId && errors.AccountHeadId}
                      disabled={false}
                    />
                  )
                ) : (
                  isView ? (
                    <TextField fullWidth size="small" label="Master" value={selectedMaster} disabled sx={fs} />
                  ) : (
                    <SearchableSelect
                      label="Master *"
                      value={form.MasterId}
                      options={masters || []}
                      onChange={val => set('MasterId', val)}
                      onBlur={() => handleBlur('MasterId')}
                      error={touched.MasterId && !!errors.MasterId}
                      helperText={touched.MasterId && errors.MasterId}
                      disabled={false}
                    />
                  )
                )}
              </Grid>

              {/* Remarks */}
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3}
                  label="Remarks"
                  value={form.Remarks || ''}
                  onChange={e => set('Remarks', e.target.value)}
                  disabled={isView}
                  inputProps={{ maxLength: 100 }}
                  sx={sxRemarks}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={sxActions}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', width: '100%' }}>
            <Box>
              {(modalAction === 'view' || modalAction === 'edit') && form.Id && onPrint && (
                <Button
                  onClick={() => onPrint(form.Id)}
                  variant="contained"
                  size="small"
                  startIcon={<Print />}
                  style={{
                    background: 'linear-gradient(135deg, #c9a84c, #9a7a2e)',
                    color: 'white', textTransform: 'none', fontWeight: 600, fontSize: '12px',
                  }}
                >
                  Download PDF
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={onClose} style={{ color: '#6c757d', textTransform: 'none', fontWeight: 500, fontSize: '12px' }}>
                Cancel
              </Button>
              {!isView && (
                <Button
                  onClick={handleSave}
                  variant="contained"
                  disabled={saving}
                  style={{
                    background: 'linear-gradient(135deg, #c9a84c, #9a7a2e)',
                    color: 'white', textTransform: 'none', fontWeight: 600, fontSize: '12px', borderRadius: '4px',
                  }}
                >
                  {saving
                    ? <CircularProgress size={14} sx={{ color: 'white' }} />
                    : modalAction === 'create' ? 'Save' : 'Update'
                  }
                </Button>
              )}
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSnackbar(p => ({ ...p, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
});

export default PettyCashModal;
